import type { KeyValueStorage } from '@dcs/platform/ports';
import { createWebStorage } from '@dcs/platform/web';
import { useStore } from 'zustand';
import { createStore, type StoreApi } from 'zustand/vanilla';

/** Licence classes the home page offers, in the order of the picker. */
export const HOME_CLASSES = ['B', 'C1', 'C', 'D'] as const;
export type HomeClass = (typeof HOME_CLASSES)[number];

/** Where the picked class lives (localStorage through the storage port). */
export const HOME_CLASS_KEY = 'dcs.home.class';
export const DEFAULT_HOME_CLASS: HomeClass = 'B';

export function isHomeClass(value: unknown): value is HomeClass {
  return HOME_CLASSES.some((licenseClass) => licenseClass === value);
}

/** `?hang=` value of a class (`features/theory/route-params.ts` `CLASS_SLUGS`). */
export const slugOf = (licenseClass: HomeClass): string => licenseClass.toLowerCase();

export interface HomeClassState {
  readonly licenseClass: HomeClass;
  setClass(licenseClass: HomeClass): void;
}

export type HomeClassStore = StoreApi<HomeClassState>;

/**
 * The class picked on the home page. Reads `dcs.home.class` once (unknown or unreadable: B) and
 * writes it on every change; a storage that throws keeps the choice in memory for the session.
 * Changes only when the user picks a class, never per frame.
 */
export function createHomeClassStore(storage: KeyValueStorage): HomeClassStore {
  let initial: HomeClass = DEFAULT_HOME_CLASS;
  try {
    const stored = storage.get(HOME_CLASS_KEY);
    if (isHomeClass(stored)) initial = stored;
  } catch {
    // Blocked storage: start from the default.
  }
  return createStore<HomeClassState>()((set) => ({
    licenseClass: initial,
    setClass(licenseClass) {
      try {
        storage.set(HOME_CLASS_KEY, licenseClass);
      } catch {
        // Not persisted; the state below still changes for this page.
      }
      set({ licenseClass });
    },
  }));
}

let shared: HomeClassStore | null = null;

/** The app-wide store over `localStorage` (in-memory fallback when blocked), built on first use. */
export function getHomeClassStore(): HomeClassStore {
  shared ??= createHomeClassStore(createWebStorage());
  return shared;
}

/** Tests only: forget the shared store so the next one reads storage again. */
export function resetHomeClassStoreForTests(): void {
  shared = null;
}

export function useHomeClass(store: HomeClassStore): HomeClassState {
  const licenseClass = useStore(store, (state) => state.licenseClass);
  const setClass = useStore(store, (state) => state.setClass);
  return { licenseClass, setClass };
}
